import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import isToday from 'dayjs/plugin/isToday'
import 'dayjs/locale/hr'
import 'dayjs/locale/en'

import { FORMATS, DIFF_UNITS } from './constants'

dayjs.extend(isToday)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(duration)
dayjs.extend(relativeTime)

export class DateService {
  constructor (config = {}) {
    this._timezone = config.timezone ?? dayjs.tz.guess()
    this._locale = config.locale ?? 'en'
    this._formats = { ...FORMATS, ...config.formats }
  }

  // ── Configuration ────────────────────────────────────────────────────────────

  /**
   * Reconfigure the service at runtime.
   * Typical use: call after auth to apply the user's timezone/locale from the backend.
   *
   * @param {object} config
   * @param {string} [config.timezone]  IANA timezone string, e.g. 'Europe/Zagreb'
   * @param {string} [config.locale]    dayjs locale string, e.g. 'hr' | 'en'
   * @param {object} [config.formats]   Partial format overrides (merged with current)
   * @returns {DateService} this — allows chaining
   */
  configure (config = {}) {
    if (config.timezone != null) this._timezone = config.timezone
    if (config.locale != null) this._locale = config.locale
    if (config.formats != null) this._formats = { ...this._formats, ...config.formats }
    return this
  }

  getConfig () {
    return {
      timezone: this._timezone,
      locale: this._locale,
      formats: { ...this._formats }
    }
  }

  // ── Locale ───────────────────────────────────────────────────────────────────

  /** Set the global dayjs locale and update the instance locale. */
  setLocale (locale) {
    dayjs.locale(locale)
    this._locale = locale
  }

  // ── Private helpers ──────────────────────────────────────────────────────────

  _d (date, fmt) {
    return fmt ? dayjs(date, fmt) : dayjs(date)
  }

  _dtz (date, fmt) {
    return this._d(date, fmt).tz(this._timezone)
  }

  // ── Formatting ───────────────────────────────────────────────────────────────

  /**
   * Format a date value, applying the configured timezone.
   *
   * @param {*}      date  Any value parseable by dayjs.
   * @param {string} fmt   Output format — always pass a FORMATS constant.
   */
  format (date, fmt) {
    return this._dtz(date).format(fmt)
  }

  /**
   * Parse a date from an explicit source format, apply timezone, output in toFormat.
   * Use when the input format differs from what dayjs would auto-detect.
   *
   * @param {string} date
   * @param {string} fromFormat  Source format (use a FORMATS constant).
   * @param {string} toFormat    Output format (use a FORMATS constant).
   */
  formatCustom (date, fromFormat, toFormat) {
    return this._dtz(date, fromFormat).format(toFormat)
  }

  /** Full ISO 8601 string — e.g. "2024-01-15T10:30:00.000Z". No timezone shift. */
  formatIsoString (date, fmt) {
    return this._d(date, fmt).toISOString()
  }

  /** Convert a UTC datetime string to the configured timezone, then format it. */
  formatFromUtc (date, fmt) {
    return dayjs.tz(date, 'UTC').tz(this._timezone).format(fmt)
  }

  /**
   * Parse a datetime that is expressed in the configured timezone and output it
   * as UTC. The inverse of {@link formatFromUtc} — use when sending a value the
   * user picked in local time to a backend that stores/compares in UTC.
   *
   * @param {string} date
   * @param {string} fromFormat  Source format (use a FORMATS constant).
   * @param {string} toFormat    Output format (use a FORMATS constant).
   */
  formatToUtc (date, fromFormat, toFormat) {
    return dayjs.tz(date, fromFormat, this._timezone).utc().format(toFormat)
  }

  // ── Current datetime ─────────────────────────────────────────────────────────

  today (fmt) {
    return dayjs().tz(this._timezone).format(fmt ?? this._formats.date)
  }

  currentTime (fmt) {
    return dayjs().tz(this._timezone).format(fmt ?? this._formats.time)
  }

  now (fmt) {
    return dayjs().tz(this._timezone).format(fmt ?? this._formats.datetime)
  }

  /** Current moment as a full ISO 8601 string (timezone-aware). */
  nowIso () {
    return dayjs().tz(this._timezone).toISOString()
  }

  // ── Parsing / conversion ─────────────────────────────────────────────────────

  /** Convert a date string from one format to another. Returns null on missing args. */
  parse (date, fromFormat, toFormat) {
    if (!date || !fromFormat || !toFormat) return null
    return dayjs(date, fromFormat).format(toFormat)
  }

  /** Parse a DMY date string (DD.MM.YYYY.) → ISO date (YYYY-MM-DD). */
  parseDmy (date) {
    return dayjs(date, this._formats.date).format(this._formats.isoDate)
  }

  /** Parse a DMY datetime string (DD.MM.YYYY. HH:mm:ss) → ISO datetime (YYYY-MM-DD HH:mm:ss). */
  parseDmyDateTime (date) {
    return dayjs(date, this._formats.datetime).format(this._formats.isoDatetime)
  }

  // ── Validation ───────────────────────────────────────────────────────────────

  /**
   * @param {string}  date
   * @param {string}  [fmt]    When provided, validates against this specific format.
   * @param {boolean} [strict] Strict mode rejects ambiguous values (e.g. month 13).
   */
  isValid (date, fmt, strict = false) {
    return fmt ? dayjs(date, fmt, strict).isValid() : dayjs(date).isValid()
  }

  isToday (date) {
    return dayjs(date).isToday()
  }

  /** Returns true if both dates fall on the same calendar day. */
  isSameDay (date1, date2) {
    return dayjs(date1).isSame(dayjs(date2), 'day')
  }

  // ── Comparison ───────────────────────────────────────────────────────────────

  /**
   * Both methods return null when either date is invalid.
   * @param {string} [fmt] Optional parse format for both dates.
   */
  isBefore (date1, date2, fmt) {
    const d1 = this._d(date1, fmt)
    const d2 = this._d(date2, fmt)
    if (!d1.isValid() || !d2.isValid()) return null
    return d1.isBefore(d2)
  }

  isAfter (date1, date2, fmt) {
    const d1 = this._d(date1, fmt)
    const d2 = this._d(date2, fmt)
    if (!d1.isValid() || !d2.isValid()) return null
    return d1.isAfter(d2)
  }

  // ── Arithmetic ───────────────────────────────────────────────────────────────

  /**
   * Add time to a date and return a formatted string.
   * Pass null/undefined as date to start from now.
   *
   * @param {string|null} date    Source date. null = current time.
   * @param {number}      amount  Amount to add.
   * @param {string}      unit    dayjs unit: 'day', 'month', 'year', 'hour', etc.
   * @param {string}      [fmt]   Output format (use a FORMATS constant). Defaults to isoDatetime.
   */
  add (date, amount, unit, fmt) {
    return (date ? dayjs(date) : dayjs()).add(amount, unit).format(fmt ?? this._formats.isoDatetime)
  }

  /**
   * Subtract time from a date and return a formatted string.
   * Pass null/undefined as date to start from now.
   *
   * @param {string|null} date    Source date. null = current time.
   * @param {number}      amount  Amount to subtract.
   * @param {string}      unit    dayjs unit: 'day', 'month', 'year', 'hour', etc.
   * @param {string}      [fmt]   Output format (use a FORMATS constant). Defaults to isoDatetime.
   */
  subtract (date, amount, unit, fmt) {
    return (date ? dayjs(date) : dayjs()).subtract(amount, unit).format(fmt ?? this._formats.isoDatetime)
  }

  /**
   * Start of a calendar unit as a formatted string.
   * Pass null/undefined as date to start from now.
   *
   * @param {string|null} date
   * @param {string}      [unit]  dayjs unit. Defaults to 'day'.
   * @param {string}      [fmt]   Output format (use a FORMATS constant). Defaults to isoDatetime.
   */
  startOf (date, unit = 'day', fmt) {
    return (date ? dayjs(date) : dayjs()).tz(this._timezone).startOf(unit).format(fmt ?? this._formats.isoDatetime)
  }

  /**
   * Start of a calendar unit as a full ISO 8601 string (timezone-aware).
   * Pass null/undefined as date to start from now.
   */
  startOfIso (date, unit = 'day') {
    return (date ? dayjs(date) : dayjs()).tz(this._timezone).startOf(unit).toISOString()
  }

  // ── Calendar helpers ─────────────────────────────────────────────────────────

  /**
   * Day of the week for a given date (0 = Sunday … 6 = Saturday).
   *
   * @param {string} date
   * @param {string} [fmt] Optional parse format.
   */
  dayOfWeek (date, fmt) {
    return this._d(date, fmt).day()
  }

  // ── Difference ───────────────────────────────────────────────────────────────

  /**
   * Numeric difference (date2 − date1) in the given unit.
   * Returns null when dates are invalid or unit is unsupported.
   *
   * @param {string} unit  One of DIFF_UNITS (e.g. 'day', 'hour', 'minute')
   * @param {string} [fmt] Optional parse format for both dates.
   */
  diff (date1, date2, unit = 'day', fmt) {
    if (!DIFF_UNITS.includes(unit)) return null
    const d1 = this._d(date1, fmt)
    const d2 = this._d(date2, fmt)
    if (!d1.isValid() || !d2.isValid()) return null
    return d2.diff(d1, unit)
  }

  /**
   * Human-readable time difference between two dates.
   *   < 24 h  → formatted string, e.g. "02:30:15"
   *   ≥ 24 h  → humanized string, e.g. "2 days", "1 month"
   *
   * @param {string} [fmt]    Format for < 24 h output. Defaults to time format.
   * @param {string} [locale] dayjs locale override. Defaults to configured locale.
   */
  timeDiff (date1, date2, fmt, locale) {
    const diffMs = dayjs(date1).diff(dayjs(date2))
    const hours = dayjs.duration(diffMs).asHours()
    const lang = locale ?? this._locale

    return hours < 24
      ? dayjs.utc(diffMs).locale(lang).format(fmt ?? this._formats.time)
      : dayjs.duration(diffMs).locale(lang).humanize()
  }
}

export function createDateService (config) {
  return new DateService(config)
}
